import { Check } from '@phosphor-icons/react';
import { ButtonCommon, InputCommon } from 'components/common';
import { useCart } from 'stores/cartContext';
import { ProductCardProps } from 'types';
import { formatPrice, getFilePath } from 'utils';
import { imageEmpty } from 'utils/importFile'; // Update this path to match your project structure

const ProductCard: React.FC<ProductCardProps> = ({ product, itemQuantity }) => {
  // Use the cart context instead of local state and props
  const { addToCart, updateQuantity, isInCart, getItemQuantity } = useCart();

  // Check if this product is in the cart
  const productInCart = isInCart(product?.id);

  // Get the quantity from cart context if the product is in cart
  const cartQuantity = getItemQuantity(product?.id);

  // Initial quantity should be what's in the cart or the minimum allowed
  const minQuantity = product?.minQuantity || 1;
  const maxQuantity = product?.maxQuantity || Infinity;

  // Handle quantity changes
  const increment = () => {
    const newQuantity = Math.min(cartQuantity + 1, maxQuantity);
    updateQuantity(product?.id, newQuantity, Number(product?.salePrice) || 0, Number(product?.costPrice) || 0);
  };

  const decrement = () => {
    const newQuantity = Math.max(cartQuantity - 1, 0);
    updateQuantity(product?.id, newQuantity, Number(product?.salePrice) || 0, Number(product?.costPrice) || 0);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) {
      const newQuantity = Math.max(Math.min(value, maxQuantity), minQuantity);
      updateQuantity(product?.id, newQuantity, Number(product?.salePrice) || 0, Number(product?.costPrice) || 0);
    }
  };

  const handleAddToCart = () => {
    if (!productInCart) {
      addToCart(product?.id, Number(product?.salePrice) || 0, Number(product?.costPrice) || 0);
    }
  };

  return (
    <div className='flex flex-col md:flex-col min-w-[300px] w-full rounded-[16px] p-[12px] overflow-hidden shadow-md bg-[var(--secondColor)] gap-[12px]'>
      <div className='w-full flex flex-row gap-[12px]'>
        <div className='min-w-[136px] max-w-[136px] min-h-[136px] max-h-[136px] rounded-[8px]'>
          <img
            src={`${product?.productImages?.[0]?.image?.filePath ? `${getFilePath()}/${product?.productImages?.[0]?.image?.filePath}` : imageEmpty}`}
            alt={product?.productImages?.[0]?.image?.name}
            className='w-[100%] h-[100%] rounded-[8px] object-cover '
          />
        </div>
        <div className='flex flex-col gap-[11px] justify-between'>
          <div className='flex flex-col gap-[11px]'>
            <p className='text-[14px] font-bold leading-[20px]'>
              {product?.name +
                (product?.color ? ` - ${product?.color}` : '') +
                (product?.dimension ? ` - ${product?.dimension}` : '') +
                (product?.sizeSpec ? ` - ${product?.sizeSpec}` : '')}
            </p>
            <p className='text-[12px] text-[var(--textBlack100)] line-clamp-3'>{product?.product?.code}</p>
            <p className='text-[12px] text-[var(--textBlack56)] line-clamp-3'>{product?.description}</p>
            <div className='inline-flex justify-start items-start gap-1'>
              <span className='text-[var(--textBalck56)] text-xs font-bold'>Thương hiệu:</span>
              <span className='text-[var(--primaryColor500)] text-xs font-medium'>{product.brand?.name || '---'}</span>
            </div>
            <div className='inline-flex justify-start items-start gap-1'>
              <span className='text-[var(--textBalck56)] text-xs font-bold'>Danh mục:</span>
              <span className='text-[var(--primaryColor500)] text-xs font-medium'>
                {product.category?.name || '---'}
              </span>
            </div>
          </div>
          {itemQuantity ? (
            <div className='flex flex-col gap-2'>
              <p className='text-[14px] font-bold leading-[20px] text-[var(--primaryColor500)]'>
                {formatPrice(Number(product?.costPrice))} VNĐ
              </p>
              <p className='text-[14px] font-bold leading-[20px] text-[var(--textBlack100)]'>
                <span className='font-medium text-[var(--textBlack56)]'>Số lượng đã đặt: </span>
                {formatPrice(Number(itemQuantity))}
              </p>
              <p className='text-[14px] font-bold leading-[20px] text-[var(--textBlack100)]'>
                <span className='font-medium text-[var(--textBlack56)]'>Còn lại: </span>
                {formatPrice(Number(product?.product?.quantity) || 0)}
              </p>
            </div>
          ) : (
            <div className='flex flex-col gap-2'>
              <p className='text-[14px] font-bold leading-[20px] text-[var(--primaryColor500)]'>
                <span className='font-medium text-[var(--textBlack56)]'>Giá bán: </span>
                {formatPrice(Number(product?.salePrice))} VNĐ
                {/* {product?.currencyUnit?.name} */}
              </p>
              <p className='text-[14px] font-bold leading-[20px] text-[var(--primaryColor500)]'>
                <span className='font-medium text-[var(--textBlack56)]'>Giá nhập: </span>
                {formatPrice(Number(product?.costPrice))} VNĐ
                {/* {product?.currencyUnit?.name} */}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className='w-full flex flex-row justify-between'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-[12px]'>
            <ButtonCommon
              onClick={decrement}
              className='flex items-center justify-center rounded-[8px]'
              color='outline'
              disabled={cartQuantity <= 0}
              size='xxs'
              style={{ width: '24px', height: '24px' }}
            >
              -
            </ButtonCommon>
            <div className=''>
              <InputCommon
                type='text'
                value={cartQuantity}
                onChange={handleInputChange}
                className='w-full text-center'
                twSize='xxs'
                style={{ width: '49px', height: '24px' }}
              />
            </div>
            <ButtonCommon
              onClick={increment}
              className='flex items-center justify-center rounded-[8px]'
              color='outline'
              disabled={cartQuantity >= maxQuantity}
              size='xxs'
              style={{ width: '24px', height: '24px' }}
            >
              +
            </ButtonCommon>
          </div>
        </div>
        <div>
          {/* Add to Cart Button */}
          <ButtonCommon
            color={productInCart ? 'success' : 'outline'}
            onClick={handleAddToCart}
            className='rounded-[8px]'
            size='xs'
            style={{
              minWidth: '150px',
              height: '32px',
              border: `${!productInCart ? '1px solid var(--primaryColor100)' : ''}`,
              color: `${!productInCart ? 'var(--primaryColor500)' : ''}`,
              transition: 'all 0.3s ease-in-out',
            }}
            iconPosition='right'
            icon={productInCart ? <Check size={16} /> : <></>}
          >
            {productInCart ? 'Đã thêm vào giỏ' : 'Thêm vào giỏ'}
          </ButtonCommon>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
